// ═══════════════════════════════════════════════════════════════════
// fountain3D.jsx — V'AEL'S PLAZA FOUNTAIN, BUILT IN BLENDER   (caP'n, 2026-09-23)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "No random glyphs etc like the picture only "V'ael's Plaza" should
// be written there. also no woman's head anywhere on the fountain, just make
// the ram's head and slap it everywhere, then use that same one and make it
// bigger so we have the one on top as well ... if you get water going proper"
//
// The stone is `public/models/fountain.glb`, built in Blender (builder:
// gamemaster/models/sculpt/builders/fountain_blender/). THE ONE RAM: every
// spout is the same head (`fp_ram_*`, one mesh in the glb), and the crowned
// ram on the column is that head, bigger. The only words on it are the
// plaque, which the game paints here (`fp_slot_plaque`).
//
// By NAME, from the glb:
//   fb_root / fp_root   the basin (squashed to the zone's ellipse [ex, sy, ez])
//                       and the pedestal (scaled evenly by sy) — the ellipse law
//   *_body, fp_ram_*    painted stone: toon + ink
//   fp_eyes_*           the rams' green eyes, lit
//   fb_crystals         the amethysts, breathing;  fb_gem  the wall gems, lit
//   fp_slot_plaque      the plaque — "V'ael's Plaza"
//   fp_spout_<name>     an empty at each ram's MOUTH, extras {dir, speed, size}:
//                       THE WATER POURS FROM THESE, so moving a ram moves its water
//
// THE WATER is all here and all shader-driven — one clock uniform, one useFrame:
//   · every fall is a real arc (thrown from the mouth, pulled down), a tapered
//     tube whose streaks FLOW down it, bright at the core, glassy at the edges
//   · the pool and the bowl swirl (the painting's violet galaxy water), sparkle,
//     and ripple in rings from wherever a stream lands
//   · a splash of droplets where each stream hits (one Points, GPU-animated)
//   · a fall that would land outside the basin is thrown softer until it lands in
//
// THE FAIL-SAFE LAW: the primitive <Fountain/> in plazaPieces3D stands in
// while the model loads, if it fails, and whenever the Plaza can't keep up.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { Fountain, rectBounds, signTex, LIGHT, G, C } from "./plazaPieces3D";
import { usePaintedBodies } from "./sculptGate";

export const FOUNTAIN_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/fountain.glb`;
const NO_INK = /eyes|crystals|fb_gem|slot_/;
const SELF_LIGHT = 0.22;
const INK_W = 0.012;
const C8 = Math.cos(Math.PI / 8);
const POOL_Y = 0.78, BOWL_Y = 2.36;           // pedestal-space water heights (the basin's pool is 0.78 in its own space too)
const POOL_A = 4.02, BOWL_A = 1.5;            // the water's octagon apothems
const GRAV = 5.5;                             // a stage gravity: the falls read as water, not as bullets

// ── the toon look (shared with plazaProp3D's numbers) ──────────────
let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([150, 150, 150, 255, 215, 215, 215, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter; RAMP.needsUpdate = true;
  return RAMP;
};
const INKS = new Map();                       // one ink material per width (a big ram needs a thinner local offset)
const inkMat = (w) => {
  const key = w.toFixed(4);
  let m = INKS.get(key);
  if (m) return m;
  m = new THREE.MeshBasicMaterial({ color: 0x120e16, side: THREE.BackSide });
  m.customProgramCacheKey = () => "fountainInk" + key;
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", `#include <begin_vertex>\n  transformed += normalize( normal ) * ${key};`);
  };
  INKS.set(key, m);
  return m;
};
const HULLS = new Map();                      // smooth-normal hull per source geometry (weld by position, then average)
function hullGeo(g) {
  let h = HULLS.get(g.uuid);
  if (h) return h;
  h = new THREE.BufferGeometry();
  h.setAttribute("position", g.attributes.position.clone());
  if (g.index) h.setIndex(g.index.clone());
  h = mergeVertices(h, 1e-4); h.computeVertexNormals();
  HULLS.set(g.uuid, h);
  return h;
}

// ── GLSL bits ───────────────────────────────────────────────────────
const NOISE = /* glsl */`
float h21(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float n21(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y); }
float fbm(vec2 p){ float a = 0.5, s = 0.0; for (int i = 0; i < 4; i++) { s += a * n21(p); p = p * 2.03 + 11.7; a *= 0.5; } return s; }
`;

// THE FALLS: streaks that run DOWN the stream, a bright core, glassy edges, foam where it lands
const fallMat = (clock) => new THREE.ShaderMaterial({
  uniforms: { uTime: clock, uCol: { value: new THREE.Color("#7d5cf0") }, uHi: { value: new THREE.Color("#efe6ff") } },
  transparent: true, depthWrite: false, side: THREE.DoubleSide,
  vertexShader: /* glsl */`
    attribute float aS; varying float vS; varying vec2 vUv; varying vec3 vN; varying vec3 vV;
    void main(){ vUv = uv; vS = aS;
      vec4 wp = modelMatrix * vec4(position, 1.0);
      vN = normalize(mat3(modelMatrix) * normal); vV = normalize(cameraPosition - wp.xyz);
      gl_Position = projectionMatrix * viewMatrix * wp; }`,
  fragmentShader: /* glsl */`
    uniform float uTime; uniform vec3 uCol; uniform vec3 uHi;
    varying float vS; varying vec2 vUv; varying vec3 vN; varying vec3 vV;
    ${NOISE}
    void main(){
      float flow = vS * 2.6 - uTime * 3.2;                         // the water runs down
      float st = n21(vec2(vUv.y * 9.0, flow)) * 0.6 + n21(vec2(vUv.y * 21.0, flow * 1.9 + 3.1)) * 0.4;
      float edge = 1.0 - abs(dot(normalize(vN), normalize(vV)));  // 0 facing us, 1 at the silhouette
      vec3 col = mix(uCol, uHi, smoothstep(0.5, 0.92, st) * 0.85 + pow(1.0 - edge, 4.0) * 0.25);
      float a = 0.38 + 0.42 * edge + 0.28 * smoothstep(0.55, 0.9, st);
      a *= smoothstep(0.0, 0.05, vUv.x);                          // born at the lip
      col = mix(col, uHi, smoothstep(0.82, 1.0, vUv.x) * 0.55);   // foam as it hits
      gl_FragColor = vec4(col, clamp(a, 0.0, 0.95));
      #include <colorspace_fragment>
    }`,
});

// THE WATER SURFACE: the painting's violet galaxy — a slow swirl, sparkles, rings where streams land
const MAX_DROPS = 12;
const waterMat = (clock, scale, apothem, drops) => {
  const d = new Array(MAX_DROPS).fill(0).map((_, i) => drops[i] ? new THREE.Vector3(drops[i][0], drops[i][1], drops[i][2]) : new THREE.Vector3(999, 999, 0));
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: clock, uScale: { value: new THREE.Vector2(scale[0], scale[1]) }, uA: { value: apothem },
      uDrops: { value: d }, uN: { value: Math.min(drops.length, MAX_DROPS) },
      uDeep: { value: new THREE.Color("#170a3c") }, uMid: { value: new THREE.Color("#4b25a8") }, uHi: { value: new THREE.Color("#c9b0ff") },
    },
    transparent: true, depthWrite: true,
    vertexShader: /* glsl */`varying vec2 vP; void main(){ vP = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */`
      uniform float uTime; uniform vec2 uScale; uniform float uA; uniform vec3 uDrops[${MAX_DROPS}]; uniform int uN;
      uniform vec3 uDeep; uniform vec3 uMid; uniform vec3 uHi;
      varying vec2 vP;
      ${NOISE}
      void main(){
        vec2 w = vec2(vP.x * uScale.x, -vP.y * uScale.y);       // the surface in plaza metres (x east, y south)
        vec2 p = vP / uA;                                       // 0 at the centre, ~1 at the wall
        float r = length(p), ang = atan(p.y, p.x);
        float sw = ang + r * 3.2 - uTime * 0.12;                // the galaxy turns
        vec2 q = vec2(cos(sw), sin(sw)) * r;
        float f = fbm(q * 3.2 + vec2(uTime * 0.04, -uTime * 0.03));
        float arms = 0.5 + 0.5 * sin(sw * 3.0 + f * 5.0);
        vec3 col = mix(uDeep, uMid, clamp(f * 1.1 + arms * 0.3 - 0.15, 0.0, 1.0));
        col += uHi * smoothstep(0.62, 0.9, f + arms * 0.18) * 0.35;           // bright swirl lines
        // rings from every stream that lands here
        for (int i = 0; i < ${MAX_DROPS}; i++) {
          if (i >= uN) break;
          vec3 dp = uDrops[i];
          float dd = distance(w, dp.xy);
          for (int k = 0; k < 2; k++) {
            float ph = fract(uTime * 0.7 + dp.z + float(k) * 0.5);
            float rr = 0.08 + ph * 0.95;
            col += uHi * exp(-pow((dd - rr) * 16.0, 2.0)) * (1.0 - ph) * 0.55;
          }
          col += uHi * exp(-dd * dd * 40.0) * 0.35;           // the churn right under the stream
        }
        // sparkles that wink on and off
        vec2 g = floor(w * 5.0); float s = h21(g);
        float tw = pow(max(0.0, sin(uTime * (1.5 + s * 2.0) + s * 40.0)), 14.0) * step(0.9, s);
        col += vec3(1.0) * tw * 0.8 * smoothstep(0.45, 0.2, length(fract(w * 5.0) - 0.5));
        col *= mix(1.0, 0.7, smoothstep(0.82, 1.0, max(abs(p.x), abs(p.y))));  // darker against the wall
        gl_FragColor = vec4(col, 0.94);
        #include <colorspace_fragment>
      }`,
  });
};

// THE SPLASH: droplets thrown up where each stream lands (all on the GPU — no per-frame work)
const PER_SPLASH = 26;
const splashMat = (clock, px) => new THREE.ShaderMaterial({
  uniforms: { uTime: clock, uPx: { value: px }, uCol: { value: new THREE.Color("#e3d6ff") } },
  transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  vertexShader: /* glsl */`
    uniform float uTime; uniform float uPx; attribute float aSeed; attribute float aPow; varying float vA;
    void main(){
      float life = fract(uTime * 1.25 + aSeed * 7.13);
      float ang = aSeed * 81.7;
      float sp = (0.2 + fract(aSeed * 91.7) * 0.5) * aPow;
      float up = (1.2 + fract(aSeed * 33.1) * 1.4) * aPow;
      vec3 p = position + vec3(cos(ang) * sp * life, up * life - 3.0 * life * life, sin(ang) * sp * life);
      p.y = max(p.y, position.y);
      vA = 1.0 - life;
      vec4 mv = modelViewMatrix * vec4(p, 1.0);
      gl_PointSize = (0.05 + 0.04 * fract(aSeed * 5.3)) * aPow * uPx / -mv.z;
      gl_Position = projectionMatrix * mv;
    }`,
  fragmentShader: /* glsl */`
    uniform vec3 uCol; varying float vA;
    void main(){ float d = length(gl_PointCoord - 0.5); if (d > 0.5) discard;
      gl_FragColor = vec4(uCol, smoothstep(0.5, 0.1, d) * vA * 0.8);
      #include <colorspace_fragment>
    }`,
});

// a tapered tube along a list of points, with arc length per vertex (for the flow)
function streamGeo(pts, r0, r1, radial = 10) {
  const n = pts.length, pos = [], nrm = [], uv = [], aS = [], idx = [];
  const T = [], len = [0];
  for (let i = 0; i < n; i++) {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
    T.push(b.clone().sub(a).normalize());
    if (i) len.push(len[i - 1] + pts[i].distanceTo(pts[i - 1]));
  }
  let N = new THREE.Vector3(0, 1, 0).cross(T[0]); if (N.lengthSq() < 1e-6) N.set(1, 0, 0); N.normalize();
  for (let i = 0; i < n; i++) {
    if (i) { N.sub(T[i].clone().multiplyScalar(N.dot(T[i]))).normalize(); }
    const B = T[i].clone().cross(N).normalize();
    const u = len[i] / len[n - 1];
    const r = r0 + (r1 - r0) * Math.pow(u, 1.4);
    for (let j = 0; j <= radial; j++) {
      const t = (j / radial) * Math.PI * 2, c = Math.cos(t), s = Math.sin(t);
      const d = N.clone().multiplyScalar(c).add(B.clone().multiplyScalar(s));
      pos.push(pts[i].x + d.x * r, pts[i].y + d.y * r, pts[i].z + d.z * r);
      nrm.push(d.x, d.y, d.z); uv.push(u, j / radial); aS.push(len[i]);
    }
  }
  for (let i = 0; i < n - 1; i++) for (let j = 0; j < radial; j++) {
    const a = i * (radial + 1) + j, b = a + radial + 1;
    idx.push(a, b, a + 1, b, b + 1, a + 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute("aS", new THREE.Float32BufferAttribute(aS, 1));
  g.setIndex(idx);
  return g;
}

// inside an octagon (flat sides to the axes) of apothem a?
const inOct = (x, z, a) => Math.max(Math.abs(x), Math.abs(z), (Math.abs(x) + Math.abs(z)) / Math.SQRT2) < a;

// throw the water from a mouth; it lands in the bowl (the crowned ram) or the pool.
// Pedestal space in, pedestal space out. Thrown softer until it lands INSIDE.
function pour(sp, E) {
  const toBowl = sp.p.y > BOWL_Y + 0.6 && inOct(sp.p.x, sp.p.z, BOWL_A + 0.2);
  const ty = toBowl ? BOWL_Y : POOL_Y;
  const ok = (x, z) => toBowl ? inOct(x, z, BOWL_A - 0.12)
    : inOct((x * E.sy) / E.ex, (z * E.sy) / E.ez, POOL_A - 0.22);  // the pool is squashed; the pedestal isn't
  let v = sp.speed, pts = null;
  for (let k = 0; k < 14; k++) {
    const vx = sp.dir[0] * v, vy = sp.dir[1] * v, vz = sp.dir[2] * v;
    const a = -GRAV / 2, bq = vy, c = sp.p.y - ty;
    const t1 = (-bq - Math.sqrt(Math.max(0, bq * bq - 4 * a * c))) / (2 * a);
    const lx = sp.p.x + vx * t1, lz = sp.p.z + vz * t1;
    if (ok(lx, lz) || k === 13) {
      pts = [];
      for (let i = 0; i <= 18; i++) {
        const t = (i / 18) * t1;
        pts.push(new THREE.Vector3(sp.p.x + vx * t, sp.p.y + vy * t - (GRAV / 2) * t * t, sp.p.z + vz * t));
      }
      break;
    }
    v *= 0.82;
  }
  return { pts, land: pts[pts.length - 1], toBowl };
}

function prepare(scene, E, plaque) {
  const root = scene.clone(true);
  const glow = [], spouts = [];
  const sign = signTex(plaque, "#f4e6c8", "#4a1a24", 512, 128, "bold 60px Georgia, serif");
  root.traverse((o) => {
    if (o.name === "fb_root") o.scale.set(E.ex, E.sy, E.ez);
    if (o.name === "fp_root") o.scale.setScalar(E.sy);
    if (/^fp_spout_/.test(o.name)) {
      const u = o.userData || {};
      spouts.push({ name: o.name.slice(9), p: o.position.clone(), dir: u.dir || [0, -0.4, 0.9], speed: u.speed ?? 1.2, size: u.size ?? 1 });
    }
  });
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material, name = o.name || "";
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    if (/slot_plaque/.test(name)) {
      const t = sign.clone(); t.flipY = false; t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true;
      o.material = new THREE.MeshBasicMaterial({ map: t, toneMapped: false });
      return;
    }
    if (/eyes/.test(name)) { o.material = new THREE.MeshBasicMaterial({ color: 0x6dff94, toneMapped: false }); return; }
    if (/fb_gem/.test(name)) { o.material = new THREE.MeshBasicMaterial({ color: 0xa870ff, toneMapped: false }); return; }
    if (/crystals/.test(name)) {
      o.material = new THREE.MeshToonMaterial({ color: m.color.clone().multiplyScalar(0.75), gradientMap: toonRamp(), emissive: new THREE.Color(C.nullus), emissiveIntensity: 0.38 });   // dark enough that the facets shade
      glow.push(o); return;
    }
    const col = m.color.clone();
    o.material = new THREE.MeshToonMaterial({
      color: col, map: m.map || null, gradientMap: toonRamp(),
      emissive: col.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    if (NO_INK.test(name)) return;
    // ink: 12 mm in the space the fountain is drawn in, whatever the node's own scale (the big ram is 2.1×)
    const ink = new THREE.Mesh(hullGeo(o.geometry), inkMat(INK_W / Math.max(1e-3, o.scale.x)));
    ink.name = name + "_ink"; ink.position.copy(o.position); ink.quaternion.copy(o.quaternion); ink.scale.copy(o.scale);
    ink.raycast = () => {};
    o.parent.add(ink);
  });
  return { root, glow, spouts };
}

function FountainModel({ rect, plaque }) {
  const b = rectBounds(rect);
  const E = useMemo(() => b ? { ex: (b.w * 0.98) / 9.8, ez: (b.d + 1.3) / 9.8, sy: 0.85 } : null, [b]);
  const gltf = useLoader(GLTFLoader, FOUNTAIN_GLB_URL);
  const { size, camera } = useThree();
  const clock = useMemo(() => ({ value: 0 }), []);
  const EE = E || { ex: 1, ez: 1, sy: 1 };
  const { root, glow, spouts } = useMemo(() => prepare(gltf.scene, EE, plaque), [gltf, EE.ex, EE.ez, EE.sy, plaque]); // eslint-disable-line react-hooks/exhaustive-deps
  const light = useRef();
  // every stream, from its mouth to where it lands
  const W = useMemo(() => {
    const falls = spouts.map((s) => ({ s, ...pour(s, EE) }));
    const fm = fallMat(clock);
    const geos = falls.map((f) => streamGeo(f.pts, 0.032 * f.s.size + 0.012, 0.05 * f.s.size + 0.03));
    // landing points in plaza metres from the fountain's centre: x east, y south, z = a phase so no two rings pulse together
    const poolDrops = falls.filter((f) => !f.toBowl).map((f, i) => [f.land.x * EE.sy, f.land.z * EE.sy, i * 0.37]);
    const bowlDrops = falls.filter((f) => f.toBowl).map((f, i) => [f.land.x * EE.sy, f.land.z * EE.sy, i * 0.37 + 0.2]);
    const pool = waterMat(clock, [EE.ex, EE.ez], POOL_A / C8, poolDrops);
    const bowl = waterMat(clock, [EE.sy, EE.sy], BOWL_A / C8, bowlDrops);
    // the splash: PER_SPLASH droplets at every landing (world-ish metres, unscaled group)
    const sp = [], seed = [], pw = [];
    falls.forEach((f, i) => { for (let k = 0; k < PER_SPLASH; k++) {
      sp.push(f.land.x * EE.sy, f.land.y * EE.sy + 0.02, f.land.z * EE.sy); seed.push(i * 0.618 + k * 0.0731); pw.push(0.55 + 0.45 * Math.min(1.6, f.s.size));
    } });
    const sg = new THREE.BufferGeometry();
    sg.setAttribute("position", new THREE.Float32BufferAttribute(sp, 3));
    sg.setAttribute("aSeed", new THREE.Float32BufferAttribute(seed, 1));
    sg.setAttribute("aPow", new THREE.Float32BufferAttribute(pw, 1));
    return { fm, geos, pool, bowl, sg };
  }, [spouts, clock]); // eslint-disable-line react-hooks/exhaustive-deps
  const splash = useMemo(() => splashMat(clock, size.height / (2 * Math.tan(((camera.fov || 50) * Math.PI) / 360))), [clock, size.height, camera.fov]);
  const poolGeo = useMemo(() => new THREE.CircleGeometry(POOL_A / C8, 8).rotateZ(Math.PI / 8), []);
  const bowlGeo = useMemo(() => new THREE.CircleGeometry(BOWL_A / C8, 8).rotateZ(Math.PI / 8), []);
  useFrame(({ clock: c }) => {
    const t = c.getElapsedTime();
    clock.value = t;
    for (let i = 0; i < glow.length; i++) glow[i].material.emissiveIntensity = 0.38 + Math.sin(t * 1.7 + i) * 0.16;   // breathing, but the facets still shade
    if (light.current) light.current.intensity = G(LIGHT.fountain) * (1 + Math.sin(t * 1.3) * 0.08);
  });
  if (!b || !E) return null;
  const { ex, ez, sy } = E;
  return (
    <group position={[b.cx, 0, b.cz]}>
      <primitive object={root} />
      {/* the pool: an octagon, flat sides to the axes like the basin, a hand below the rim */}
      <mesh geometry={poolGeo} material={W.pool} position={[0, POOL_Y * sy, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[ex, ez, 1]} renderOrder={1} />
      <group scale={[sy, sy, sy]}>
        <mesh geometry={bowlGeo} material={W.bowl} position={[0, BOWL_Y, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={1} />
        {W.geos.map((g, i) => <mesh key={i} geometry={g} material={W.fm} renderOrder={2} />)}
        {/* the glow that lights the plaza — ONE light, as before */}
        <pointLight ref={light} position={[0, 2.6, 0]} intensity={G(LIGHT.fountain)} distance={22} decay={1.4} color="#bca4ff" castShadow={false} />
      </group>
      <points geometry={W.sg} material={splash} renderOrder={3} frustumCulled={false} />
      {/* the pool of light on the stones */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]} scale={[ex, ez, 1]}>
        <ringGeometry args={[5.35, 7.4, 8]} />
        <meshBasicMaterial color={C.nullus} transparent opacity={0.07} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[fountain] fountain.glb did not load — the primitive fountain stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

// THE FAIL-SAFE LAW: the primitive fountain while loading, on failure, and when the Plaza can't keep up
export function SculptedFountain({ rect, plaque = "V'ael's Plaza" }) {
  const painted = usePaintedBodies();
  const fb = <Fountain rect={rect} plaque={plaque} />;
  if (painted) return fb;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <FountainModel rect={rect} plaque={plaque} />
      </Suspense>
    </GlbBoundary>
  );
}
export default SculptedFountain;
